/**
 * Admin Feature - Global Store (Zustand)
 *
 * Estado global do módulo admin
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi, metricsApi } from './admin.api'
import { setAuthToken, clearAuthToken, initializeAuth } from '../../../app/shared/utils/api'

const useAdminStore = create(
	persist(
		(set, get) => ({
			// =====================================================
			// STATE
			// =====================================================
			admin: null,
			isAuthenticated: false,
			isReady: false, // Indica se a inicialização foi concluída
			loading: false,
			error: null,

			// Metrics cache
			metrics: {
				overview: null,
				lastFetched: null,
			},

			// UI State
			sidebarOpen: true,
			currentPage: 'dashboard',

			// =====================================================
			// AUTH ACTIONS
			// =====================================================
			
			/**
			 * Inicializa a autenticação a partir do localStorage
			 * Similar ao initializeUser do app mobile
			 */
			initializeAuth: async () => {
				const hasToken = initializeAuth() // Configura o header do axios
				
				if (!hasToken) {
					set({ isReady: true, isAuthenticated: false, admin: null })
					return false
				}
				
				try {
					// Valida o token buscando os dados do admin
					const response = await authApi.getMe()
					const adminData = response._id ? response : (response.admin || response.user)
					
					set({ 
						admin: adminData, 
						isAuthenticated: true, 
						isReady: true 
					})
					return true
				} catch (error) {
					// Token inválido, limpa tudo
					clearAuthToken()
					set({ 
						isReady: true, 
						isAuthenticated: false, 
						admin: null 
					})
					return false
				}
			},

			login: async (email, password) => {
				set({ loading: true, error: null })

				try {
					const { token, user } = await authApi.login({ email, password })

					// Usa a função centralizada para salvar o token
					setAuthToken(token)

					set({
						admin: user,
						isAuthenticated: true,
						loading: false,
					})

					return { success: true }
				} catch (error) {
					const errorMessage = error.response?.data?.error || 'Erro ao fazer login'
					set({ loading: false, error: errorMessage })
					return { success: false, error: errorMessage }
				}
			},

			logout: () => {
				clearAuthToken()
				set({
					admin: null,
					isAuthenticated: false,
					metrics: { overview: null, lastFetched: null },
				})
			},

			checkAuth: async () => {
				const token = localStorage.getItem('admin_token')
				if (!token) {
					set({ isAuthenticated: false, admin: null })
					return false
				}

				try {
					const response = await authApi.getMe()
					const adminData = response._id ? response : (response.admin || response.user)
					set({ admin: adminData, isAuthenticated: true })
					return true
				} catch (error) {
					const status = error.response?.status
					if (status === 401 || status === 403) {
						clearAuthToken()
						set({ isAuthenticated: false, admin: null })
					}
					return false
				}
			},

			// =====================================================
			// METRICS ACTIONS
			// =====================================================
			fetchOverview: async (force = false) => {
				const state = get()

				// Cache de 5 minutos
				const cacheValid = state.metrics.lastFetched &&
					Date.now() - state.metrics.lastFetched < 5 * 60 * 1000

				if (!force && cacheValid && state.metrics.overview) {
					return state.metrics.overview
				}

				try {
					const overview = await metricsApi.getOverview()
					set({
						metrics: {
							overview,
							lastFetched: Date.now(),
						},
					})
					return overview
				} catch (error) {
					console.error('Erro ao buscar métricas:', error)
					return null
				}
			},

			// =====================================================
			// UI ACTIONS
			// =====================================================
			toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

			setCurrentPage: (page) => set({ currentPage: page }),

			clearError: () => set({ error: null }),
		}),
		{
			name: 'admin-storage',
			partialize: (state) => ({
				sidebarOpen: state.sidebarOpen,
			}),
		}
	)
)

export { useAdminStore }
export default useAdminStore
